import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldOff, ExternalLink, AlertTriangle, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useMcAccessStatus } from '@/hooks/useMcAccessStatus';
import { useSelection } from '@/contexts/SelectionContext';
import { useNotifications } from '@/contexts/NotificationContext';

// Blocking modal that pops up once per incident when the active Merchant Center
// has lost GMC access. The user must explicitly pick one of three paths:
//   - "Daten behalten"  → override_mc_deletion RPC → cancels 30-day timer
//   - "Verstanden"      → dismiss_mc_access_modal RPC → modal closes, banner persists
//   - "Konto jetzt löschen" → delete_merchant_center RPC → cascade delete
//
// The 4th implicit path is "do nothing": after 30d the cron deletes the MC + all data.

export function AccessRevokedOverlay() {
  const { t } = useTranslation('optimization');
  const {
    mcId,
    mcName,
    shouldShowBlockingModal,
    daysUntilDeletion,
  } = useMcAccessStatus();
  const { clearSelection } = useSelection();
  const queryClient = useQueryClient();
  const { toast } = useNotifications();
  const [busy, setBusy] = useState<null | 'keep' | 'dismiss' | 'delete'>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!shouldShowBlockingModal || !mcId) return null;

  const displayName = mcName ?? t('accessRevoked.overlay.titleFallback');
  const confirmDisplayName = mcName ?? t('accessRevoked.overlay.confirmDeleteFallback');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mc-access-lifecycle', mcId] });
  };

  const handleKeep = async () => {
    setBusy('keep');
    const { error } = await supabase.rpc('override_mc_deletion', { p_mc_id: mcId });
    setBusy(null);
    if (error) {
      toast({ title: t('accessRevoked.overlay.toast.errorTitle'), description: error.message, variant: 'destructive' });
      return;
    }
    toast({
      tone: 'success',
      title: t('accessRevoked.overlay.toast.keptTitle'),
      description: t('accessRevoked.overlay.toast.keptDesc'),
    });
    invalidate();
  };

  const handleDismiss = async () => {
    setBusy('dismiss');
    const { error } = await supabase.rpc('dismiss_mc_access_modal', { p_mc_id: mcId });
    setBusy(null);
    if (error) {
      toast({ title: t('accessRevoked.overlay.toast.errorTitle'), description: error.message, variant: 'destructive' });
      return;
    }
    invalidate();
  };

  const handleDelete = async () => {
    setBusy('delete');
    const { error } = await supabase.rpc('delete_merchant_center', { p_mc_id: mcId });
    setBusy(null);
    if (error) {
      toast({ title: t('accessRevoked.overlay.toast.errorTitle'), description: error.message, variant: 'destructive' });
      return;
    }
    toast({
      tone: 'success',
      title: t('accessRevoked.overlay.toast.deletedTitle'),
      description: t('accessRevoked.overlay.toast.deletedDesc', {
        mcName: mcName ?? t('accessRevoked.overlay.toast.deletedFallback'),
      }),
    });
    // Selection muss neu laufen, weil die aktive MC nicht mehr existiert.
    await clearSelection();
    invalidate();
  };

  return (
    <Dialog open={shouldShowBlockingModal}>
      <DialogContent
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="max-w-2xl border-destructive/30"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive-ink">
              <ShieldOff className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle className="text-xl">
                {t('accessRevoked.overlay.title', { mcName: displayName })}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {t('accessRevoked.overlay.subtitle')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-destructive-ink">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <Trans
                  i18nKey="accessRevoked.overlay.consequences"
                  t={t}
                  components={{ bold: <strong /> }}
                />
              </div>
            </div>
          </div>

          {daysUntilDeletion !== null && (
            <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
              <strong>
                {daysUntilDeletion > 0
                  ? t('accessRevoked.overlay.deletionWarning', { count: daysUntilDeletion })
                  : t('accessRevoked.overlay.deletionWarningSoon')}
              </strong>
              <div className="mt-1 text-xs text-muted-foreground">
                {t('accessRevoked.overlay.deletionScope')}
              </div>
            </div>
          )}

          <div className="rounded-md bg-muted/40 p-3 text-xs">
            <div className="mb-1 font-medium">{t('accessRevoked.overlay.howToRestoreTitle')}</div>
            <ol className="list-decimal pl-4 leading-relaxed">
              <li>
                <Trans
                  i18nKey="accessRevoked.overlay.step1"
                  t={t}
                  components={{
                    // Tag-Name muss zum Locale-String passen (`<gmcLink>` in
                    // optimization.json) -- `<link>` ist ein HTML-Void-Element
                    // und wird vom Parser sofort selbst geschlossen (Task 11,
                    // Befund 1; siehe UploadConfirmDialog.tsx fuer Details).
                    gmcLink: (
                      <a
                        href="https://merchants.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary-ink underline-offset-2 hover:underline"
                      />
                    ),
                  }}
                />{' '}
                <ExternalLink className="inline h-3 w-3" />
              </li>
              <li>
                <Trans
                  i18nKey="accessRevoked.overlay.step2"
                  t={t}
                  components={{ bold: <strong /> }}
                />
              </li>
              <li>{t('accessRevoked.overlay.step3')}</li>
              <li>{t('accessRevoked.overlay.step4')}</li>
            </ol>
          </div>
        </div>

        {!confirmDelete ? (
          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive-ink hover:bg-destructive-light"
              onClick={() => setConfirmDelete(true)}
              disabled={!!busy}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('accessRevoked.overlay.deleteNowCta')}
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={handleDismiss}
                disabled={!!busy}
              >
                {t('accessRevoked.overlay.dismissCta')}
              </Button>
              <Button
                type="button"
                onClick={handleKeep}
                disabled={!!busy}
              >
                {busy === 'keep' ? t('accessRevoked.overlay.savingCta') : t('accessRevoked.overlay.keepCta')}
              </Button>
            </div>
          </DialogFooter>
        ) : (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4">
            <div className="mb-3 text-sm">
              <strong>{t('accessRevoked.overlay.confirmDeleteTitle')}</strong>{' '}
              {t('accessRevoked.overlay.confirmDeleteBody', { mcName: confirmDisplayName })}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
                disabled={!!busy}
              >
                {t('accessRevoked.overlay.cancelCta')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={!!busy}
              >
                {busy === 'delete' ? t('accessRevoked.overlay.deletingCta') : t('accessRevoked.overlay.confirmDeleteCta')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
